// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { makeAutoObservable, when } from "mobx";
import qs from "query-string";

import { SystemId } from "~datatypes";

import {
  FilterConfig,
  StaffFilter,
  WorkflowsTasksConfig,
} from "../../core/models/types";
import { ClientDetailComponentName } from "../../core/WorkflowsJusticeInvolvedPersonProfile/OpportunityProfile";
import { SupervisionTaskCategory } from "../../core/WorkflowsTasks/fixtures";
import { TaskTableColumnId } from "../../core/WorkflowsTasks/TasksTable";
import { CombinedUserRecord } from "../../FirestoreStore";
import { TenantConfigs } from "../../tenants";
import { StaffFilterFunction } from "../../WorkflowsStore";
import { RootStore } from "../index";
import {
  FeatureVariantRecord,
  isTenantId,
  LanternMethodology,
  LanternTenants,
  TenantId,
} from "../types";
import UserStore from "../UserStore";
import getDistrictKeyMap, { DistrictKeyMap } from "./districtKeyMappings";
import { LANTERN_TENANTS } from "./lanternTenants";
import methodology from "./methodology";

type TenantConfigLabelType =
  | "supervisionEndDateCopy"
  | "incarcerationStaffTitle"
  | "releaseDateCopy"
  | "assignedToCopy";

export const CURRENT_TENANT_IN_SESSION = "adminUserCurrentTenantInSession";
const TENANT_ID_QUERY_PARAM = "tenantId";

/*
 * Returns the current state that should be viewed, in priority of:
 *  1. The tenantId query param, if set (Recidiviz users only)
 *  2. The sessionStorage cache if already set
 *  3. The first available state in alphabetical order.
 */
function getTenantIdFromUser(userStore: UserStore): TenantId | undefined {
  const storageStateCode = sessionStorage.getItem(CURRENT_TENANT_IN_SESSION) as
    | TenantId
    | undefined;

  if (userStore.user) {
    const { availableStateCodes, userHasAccess, isRecidivizUser } = userStore;

    if (isRecidivizUser) {
      const queryTenantId = getTenantIdFromQuery();
      if (queryTenantId) {
        return queryTenantId;
      }
    }

    if (storageStateCode && userHasAccess(storageStateCode)) {
      return storageStateCode;
    }
    return availableStateCodes[0] as TenantId;
  }

  return storageStateCode;
}

function getTenantIdFromQuery(): TenantId | undefined {
  const query = qs.parse(window.location.search);
  const queryTenantId = query[TENANT_ID_QUERY_PARAM] as string;

  if (isTenantId(queryTenantId)) {
    return queryTenantId;
  }
}

const defaultSupervisionStaffFilterFunction: StaffFilterFunction = (
  user: CombinedUserRecord,
) => {
  return user.updates?.overrideDistrictIds
    ? ({
        filterField: "district",
        filterValues: user.updates.overrideDistrictIds,
      } as StaffFilter)
    : undefined;
};

export default class TenantStore {
  rootStore;

  currentTenantId?: TenantId;
  tenantConfigs: TenantConfigs;

  constructor({
    rootStore,
    tenantConfigs,
  }: {
    rootStore: RootStore;
    tenantConfigs: TenantConfigs;
  }) {
    makeAutoObservable(this);

    this.rootStore = rootStore;
    this.tenantConfigs = tenantConfigs;

    when(
      () => !this.rootStore.userStore.userIsLoading,
      () =>
        this.setCurrentTenantId(getTenantIdFromUser(this.rootStore.userStore)),
    );
  }

  /**
   * Saves the tenant ID in the query, if it isn't already (Recidiviz users only)
   * Gets called every navigation change by {@link ProtectedLayout}
   */
  saveTenantIdToQuery() {
    when(
      () => this.rootStore.userStore.isAuthorized,
      () => {
        if (!this.rootStore.userStore.isRecidivizUser) {
          return;
        }

        const tenantId = getTenantIdFromUser(this.rootStore.userStore);
        const queryTenenant = getTenantIdFromQuery();

        // Since the tenant ID query get/set system is outside the rest of the routing,
        // we directly manipulate the page's query string
        if (tenantId && queryTenenant !== tenantId) {
          const url = new URL(location.href);
          url.searchParams.set(TENANT_ID_QUERY_PARAM, tenantId);
          window.history.replaceState(window.history.state, "", url.toString());
        }
      },
    );
  }

  setCurrentTenantId(tenantId: TenantId | undefined): void {
    this.currentTenantId = tenantId;
    if (tenantId) {
      sessionStorage.setItem(CURRENT_TENANT_IN_SESSION, tenantId);
      this.saveTenantIdToQuery();
    }
  }

  get isLanternTenant(): boolean {
    if (!this.currentTenantId) return false;
    const tenantId = this.currentTenantId as LanternTenants;
    return LANTERN_TENANTS.includes(tenantId);
  }

  get tenantMappings(): DistrictKeyMap {
    if (!this.currentTenantId) return {};
    return getDistrictKeyMap(this.currentTenantId);
  }

  get methodology(): LanternMethodology {
    if (!this.currentTenantId) return {};
    const tenantId = this.currentTenantId as LanternTenants;
    return methodology[tenantId];
  }

  get stateName(): string {
    if (!this.currentTenantId) return "";
    return this.tenantConfigs[this.currentTenantId].name;
  }

  get stateCode(): string {
    if (!this.currentTenantId) return "";
    return this.tenantConfigs[this.currentTenantId].stateCode;
  }

  get domain(): string | undefined {
    if (!this.currentTenantId) return "";
    return this.tenantConfigs[this.currentTenantId].domain;
  }

  get enableUserRestrictions(): boolean {
    if (!this.currentTenantId) return false;
    return (
      this.tenantConfigs[this.currentTenantId].enableUserRestrictions &&
      Array.isArray(this.rootStore.userStore.allowedSupervisionLocationIds) &&
      this.rootStore.userStore.allowedSupervisionLocationIds.length > 0
    );
  }

  get pathwaysName(): string {
    if (!this.currentTenantId) return "";
    return (
      this.tenantConfigs[this.currentTenantId].pathwaysNameOverride ??
      "System-Level Trends"
    );
  }

  get insightsLaunchedDistricts(): string[] | undefined {
    if (!this.currentTenantId) return;
    return this.tenantConfigs[this.currentTenantId].insightsLaunchedDistricts;
  }

  get insightsLanternState(): boolean {
    if (!this.currentTenantId) return false;
    return (
      this.tenantConfigs[this.currentTenantId].insightsLanternState ?? false
    );
  }

  get taskCategories(): SupervisionTaskCategory[] {
    const tasksDefault: SupervisionTaskCategory[] = [
      "ALL_TASKS",
      "OVERDUE",
      "DUE_THIS_WEEK",
      "DUE_THIS_MONTH",
    ];
    return this.tasksConfiguration?.categories ?? tasksDefault;
  }

  get workflowsOpportunityFilterConfig(): FilterConfig | undefined {
    if (!this.currentTenantId) return;

    return this.tenantConfigs[this.currentTenantId]
      .workflowsOpportunityFilterConfig;
  }

  get tasksConfiguration(): WorkflowsTasksConfig | undefined {
    if (!this.currentTenantId) return;

    return this.tenantConfigs[this.currentTenantId].workflowsTasksConfig;
  }

  get tasksTableColumns(): TaskTableColumnId[] {
    return (
      this.tasksConfiguration?.columns ?? [
        "name",
        "id",
        "task",
        "dueDate",
        "frequency",
        "supervisionLevel",
        "caseType",
        "tasksDue",
      ]
    );
  }

  get tasksSidebarComponents(): ClientDetailComponentName[] {
    return (
      this.tasksConfiguration?.sideBarComponents ?? [
        "Supervision",
        "Milestones",
        "Contact",
      ]
    );
  }
  get tasksPageDescriptionMarkdown(): string {
    return (
      this.tasksConfiguration?.pageDescriptionMarkdown ??
      "The clients below might have upcoming requirements this month."
    );
  }

  /**
   * Config-driven "Additional information" sections for the main Client
   * Profile page (`FullProfile.tsx`), if this tenant has opted in — see
   * `TenantConfig.clientProfileConfig`. `undefined` when the tenant hasn't
   * defined one, so `FullProfile.tsx` can fall back to its legacy hardcoded
   * composition.
   */
  get clientProfileSections(): ClientDetailComponentName[] | undefined {
    if (!this.currentTenantId) return undefined;

    return this.tenantConfigs[this.currentTenantId].clientProfileConfig
      ?.sections;
  }

  /** Right-column counterpart to `clientProfileSections` — see
   * `TenantConfig.clientProfileConfig.rightColumnSections`. */
  get clientProfileRightColumnSections():
    | ClientDetailComponentName[]
    | undefined {
    if (!this.currentTenantId) return undefined;

    return this.tenantConfigs[this.currentTenantId].clientProfileConfig
      ?.rightColumnSections;
  }

  /**
   * Returns the boolean from the tenant config which specifies whether to display the supervisor's
   * supervisionUnit rather than the supervisionDistrict on the Supervisor Page (and Staff page in legacy layout)
   */
  get insightsUnitState(): boolean {
    if (!this.currentTenantId) return false;
    return !!this.tenantConfigs[this.currentTenantId].insightsUnitState;
  }

  /**
   * Returns the function used to filter which staff members a user can search for in a specific system.
   * First checks for a per-system filter in workflowsSystemConfigs, then falls back to the default filter function.
   */
  getWorkflowsStaffFilterFnForSystem(
    systemId: SystemId | undefined,
  ): StaffFilterFunction {
    if (!this.currentTenantId) return () => undefined;

    const config = this.tenantConfigs[this.currentTenantId];
    // Get the per-system config if a specific system is requested, or return undefined for "ALL" systems
    const systemConfig =
      systemId && systemId !== "ALL"
        ? config?.workflowsSystemConfigs?.[systemId]
        : undefined;

    /*  Return the per-system staff filter function if it exists,
        otherwise return the default supervision staff filter function for SUPERVISION system,
        or undefined for INCARCERATION */
    return (
      systemConfig?.staffFilterFn ??
      (systemId === "SUPERVISION"
        ? defaultSupervisionStaffFilterFunction
        : () => undefined)
    );
  }

  get DOCName(): string {
    const config = this.currentTenantId
      ? this.tenantConfigs[this.currentTenantId]
      : undefined;
    return config?.DOCName ?? "DOC";
  }

  /**
   * Returns the appropriate display ID copy based on system type and tenant configuration.
   * Defaults to "${DOCName} ID" if no specific override is configured.
   */
  getDisplayIdCopy(systemType: SystemId | undefined): string {
    const config = this.currentTenantId
      ? this.tenantConfigs[this.currentTenantId]
      : undefined;

    if (systemType === "SUPERVISION" && config?.supervisionDisplayIdCopy) {
      return config.supervisionDisplayIdCopy;
    }
    if (systemType === "INCARCERATION" && config?.facilitiesDisplayIdCopy) {
      return config.facilitiesDisplayIdCopy;
    }
    return `${this.DOCName} ID`;
  }

  // Copy used for the Workflows tool, with reasonable defaults
  get labels(): Record<TenantConfigLabelType, string> {
    const config = this.currentTenantId
      ? this.tenantConfigs[this.currentTenantId]
      : undefined;
    return {
      releaseDateCopy:
        config?.releaseDateCopyOverride ?? "Full-term Release Date",
      supervisionEndDateCopy: config?.supervisionEndCopyOverride ?? "End",
      incarcerationStaffTitle:
        config?.incarcerationStaffTitleOverride ?? "Case Manager",
      assignedToCopy: config?.assignedToCopy ?? "Assigned to",
    };
  }

  get tenantFeatureVariants(): FeatureVariantRecord {
    if (!this.currentTenantId) return {};
    return this.tenantConfigs[this.currentTenantId].featureVariants ?? {};
  }

  get workflowsMethodologyUrl(): string | undefined {
    if (!this.currentTenantId) return;
    return this.tenantConfigs[this.currentTenantId].workflowsMethodologyUrl;
  }

  get currentTenantConfig() {
    if (!this.currentTenantId) return;
    return this.tenantConfigs[this.currentTenantId];
  }

  get insightsNumDaysWithoutLogin(): number {
    return this.currentTenantConfig?.insightsNumDaysWithoutLogin ?? 30;
  }

  get directorDashboardConfig() {
    return this.currentTenantConfig?.directorDashboard;
  }
}
